
import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { MAP_STYLE } from '../components/map/MapUtils';
import { useIsMobile } from './use-mobile';
import { Region } from '../data/types/region-types';
import { City } from '../data/types/city-types';
import { MAPBOX_TOKEN } from '../utils/api-keys';

interface UseMapInitializationProps {
  mapContainer: React.RefObject<HTMLDivElement>;
  selectedRegion: Region | null | undefined;
  selectedCity: City | null | undefined;
}

export function useMapInitialization({ 
  mapContainer, 
  selectedRegion, 
  selectedCity 
}: UseMapInitializationProps) {
  const map = useRef<mapboxgl.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapZoomLevel, setMapZoomLevel] = useState<'country' | 'region' | 'city'>('country');
  const { isMobile, orientation } = useIsMobile();
  
  useEffect(() => {
    const initializeMap = () => {
      if (!mapContainer.current || map.current) return;

      console.log("Initializing map with Mapbox token:", MAPBOX_TOKEN ? "Token exists" : "No token");
      
      try {
        mapboxgl.accessToken = MAPBOX_TOKEN;
        
        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: MAP_STYLE,
          center: [0, 20],
          zoom: isMobile ? 1.8 : 2.2,
          projection: 'mercator',
          pitch: 0,
          attributionControl: false,
          fadeDuration: 0, // Disable fade transitions for immediate visibility
          renderWorldCopies: true,
          preserveDrawingBuffer: true, // This helps with rendering issues in some browsers
        });
        
        map.current.addControl(
          new mapboxgl.NavigationControl({
            visualizePitch: true,
            showCompass: isMobile ? false : true,
            showZoom: true,
          }),
          'top-right'
        );

        // More immediate zoom level detection
        map.current.on('zoom', () => {
          if (!map.current) return;
          
          const zoom = map.current.getZoom();
          // Updated zoom thresholds to ensure proper level detection
          if (zoom < 5) {
            setMapZoomLevel('country');
          } else if (zoom < 9) {
            setMapZoomLevel('region');
          } else {
            setMapZoomLevel('city');
          }
          
          // Force immediate visibility update for markers when zoom changes
          const container = map.current.getContainer();
          if (zoom >= 5 && container) {
            container.classList.add('region-view');
            
            // Immediately show city markers and hide region labels
            setTimeout(() => {
              document.querySelectorAll('.city-marker, .mapboxgl-marker, .city-marker-funding').forEach((el) => {
                if (el instanceof HTMLElement) {
                  el.style.cssText += `
                    display: block !important;
                    visibility: visible !important;
                    opacity: 1 !important;
                    z-index: 500 !important;
                  `;
                }
              });
            }, 10);
          } else if (zoom < 5 && container) {
            container.classList.remove('region-view');
          }
        });

        map.current.on('load', () => {
          console.log("Map loaded event fired");
          setMapLoaded(true);
          
          if (map.current) {
            // Add a workaround for ensuring everything is visible
            setTimeout(() => {
              if (map.current) {
                map.current.resize();
                
                const mapContainer = map.current.getContainer();
                if (mapContainer) {
                  mapContainer.style.visibility = 'visible';
                  mapContainer.style.height = '100%';
                }
                
                // Add a custom event that can be used to force render markers
                map.current.fire('forcemarkers');
              }
            }, 100);
          }
        });
        
        // Add a specific handler for when a region is clicked to ensure markers are visible
        map.current.on('forcemarkers', () => {
          console.log('Force markers event fired, ensuring all markers are visible');
          setTimeout(() => {
            document.querySelectorAll('.city-marker, .mapboxgl-marker, .city-marker-funding').forEach((el) => {
              if (el instanceof HTMLElement) {
                el.style.cssText += `
                  display: block !important;
                  visibility: visible !important;
                  opacity: 1 !important;
                  z-index: 1000 !important;
                `;
              }
            });
          }, 200);
        });

        // Handle style loading errors gracefully
        map.current.on('error', (e) => {
          console.error("Mapbox error:", e);
          
          // Attempt to recover if style failed to load
          if (e.error && e.error.message && e.error.message.includes('style')) {
            console.log("Attempting to recover from style loading error");
            setTimeout(() => {
              if (map.current) map.current.resize();
            }, 500);
          }
        });

        if (isMobile) {
          setTimeout(() => {
            const controls = document.querySelectorAll('.mapboxgl-ctrl-group button');
            controls.forEach(control => {
              if (control instanceof HTMLElement) {
                control.style.width = '44px';
                control.style.height = '44px';
              }
            });
          }, 500);
        }
      } catch (error) {
        console.error("Error initializing map:", error);
      }
    };

    initializeMap();

    return () => {
      if (map.current) {
        console.log("Removing map instance on cleanup");
        map.current.remove();
        map.current = null;
      }
    };
  }, [isMobile]);

  // Add effect to force visibility of city markers when region changes
  useEffect(() => {
    if (map.current && mapLoaded && selectedRegion) {
      console.log("Region selected, ensuring city markers are visible");
      
      // Add a slight delay to ensure DOM is ready
      setTimeout(() => {
        document.querySelectorAll('.city-marker, .mapboxgl-marker, .city-marker-funding').forEach((el) => {
          if (el instanceof HTMLElement) {
            el.style.cssText += `
              display: block !important;
              visibility: visible !important;
              opacity: 1 !important;
              z-index: 1000 !important;
              transition: none !important;
            `;
          }
        });
        
        // Update map container class
        const container = map.current?.getContainer();
        if (container) {
          container.classList.add('region-view');
          container.classList.remove('mexico-view');
        }
        
        // Fire the custom event to ensure markers are visible
        map.current.fire('forcemarkers');
      }, 200);
    }
  }, [selectedRegion, mapLoaded]);

  return { map, mapLoaded, mapZoomLevel, setMapZoomLevel };
}
